import {z} from 'zod';
import {
	createZodLiterals,
	editEffectsSortOrder,
	type EditEffectsSortOrderParams,
	getSortField,
	type GetSortFieldParams,
	getSortOrder,
	type GetSortOrderParams,
	SortOrder,
	SortSchema,
} from '@netx/core-schema';
import {
	UploadAssetRequestField,
	type IUploadAssetRequest,
} from './uploadAssetRequestSchema';

export const UPLOAD_ASSET_REQUEST_SORTABLE_FIELDS = [
	UploadAssetRequestField.title,
	UploadAssetRequestField.creationDate,
	UploadAssetRequestField.expirationDate,
	UploadAssetRequestField.status,
	UploadAssetRequestField.categoryName,
	UploadAssetRequestField.creatorUserLabel,
	UploadAssetRequestField.notifyLabel,
] as const;

export const UploadAssetRequestsSortSchema = SortSchema.omit({
	field: true,
}).extend({
	field: z.union(createZodLiterals(...UPLOAD_ASSET_REQUEST_SORTABLE_FIELDS)),
});

export type UploadAssetRequestsSort = z.infer<
	typeof UploadAssetRequestsSortSchema
>;

export const UploadAssetRequestsSortableField = z.enum(
	UPLOAD_ASSET_REQUEST_SORTABLE_FIELDS,
).enum;

export const UPLOAD_ASSET_REQUESTS_DEFAULT_SORT_FIELD =
	UploadAssetRequestsSortableField.creationDate;

export const UPLOAD_ASSET_REQUESTS_DEFAULT_SORT_ORDER = SortOrder.DESCENDING;

/** will return the sort passed in if valid/specified, otherwise will return the defaults for the invalid/unspecified */
export const getUploadAssetRequestsDefaultSort = (
	sort: Partial<UploadAssetRequestsSort> = {},
): UploadAssetRequestsSort => ({
	field: getUploadAssetRequestsSortField({sortField: sort.field}),
	order: getUploadAssetRequestsSortOrder({sortOrder: sort.order}),
});

/** will return the passed in sort field if valid, otherwise will retutn the default sort field */
export const getUploadAssetRequestsSortField = (
	params: Omit<
		GetSortFieldParams<(typeof UPLOAD_ASSET_REQUEST_SORTABLE_FIELDS)[number]>,
		'defaultSortField' | 'sortableFields'
	>,
) =>
	getSortField({
		...params,
		sortableFields: UPLOAD_ASSET_REQUEST_SORTABLE_FIELDS,
		defaultSortField: UPLOAD_ASSET_REQUESTS_DEFAULT_SORT_FIELD,
	});

/** will return the passed in sort order if valid, otherwise will return the default sort order */
export const getUploadAssetRequestsSortOrder = (
	params: Omit<GetSortOrderParams, 'defaultSortOrder'>,
) =>
	getSortOrder({
		...params,
		defaultSortOrder: UPLOAD_ASSET_REQUESTS_DEFAULT_SORT_ORDER,
	});

/** given the original data, the new data, and the current sort, determine if the sort order was effected */
export const uploadAssetRequestEditEffectsOrder = (
	params: Omit<
		EditEffectsSortOrderParams<
			IUploadAssetRequest,
			(typeof UPLOAD_ASSET_REQUEST_SORTABLE_FIELDS)[number]
		>,
		'sortableFields'
	>,
) =>
	editEffectsSortOrder({
		...params,
		sortableFields: UPLOAD_ASSET_REQUEST_SORTABLE_FIELDS,
	});
